import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import clsx from 'clsx';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { Card, SectionHeader } from '@/components/Card';
import { MediaUpload } from '@/components/MediaUpload';
import { facebookAdSetsService, FbAdSet } from '@/services/facebook-ad-sets.service';
import { facebookAdCreativesService, FbCreative, CreateAdCreativePayload } from '@/services/facebook-ad-creatives.service';
import { facebookAdsService } from '@/services/facebook-ads.service';
import { facebookAdAccountService, MetaAdAccount } from '@/services/facebook-ad-account.service';
import { CreativePreview } from '@/components/facebook/CreativePreview';
import { aiCampaignService } from '@/services/ai-campaign.service';
import { ModelPicker, ModelPickerValue } from '@/components/ModelPicker';
import toast from 'react-hot-toast';
import {
  AlertTriangle, Megaphone, Image as ImageIcon, Link2, Film, Search, Check,
  ExternalLink, Plus, Eye, Wand2,
} from 'lucide-react';

type CreativeMode = 'pick' | 'new';
type MediaMode = 'image' | 'video';

const CTAS: Array<{ value: NonNullable<CreateAdCreativePayload['call_to_action_type']>; label: string }> = [
  { value: 'LEARN_MORE', label: 'Learn More' },
  { value: 'SHOP_NOW', label: 'Shop Now' },
  { value: 'SIGN_UP', label: 'Sign Up' },
  { value: 'CONTACT_US', label: 'Contact Us' },
  { value: 'GET_QUOTE', label: 'Get Quote' },
  { value: 'BOOK_TRAVEL', label: 'Book Now' },
  { value: 'DOWNLOAD', label: 'Download' },
  { value: 'INSTALL_MOBILE_APP', label: 'Install App' },
  { value: 'USE_APP', label: 'Open App' },
  { value: 'WHATSAPP_MESSAGE', label: 'Send WhatsApp Message' },
];

export default function AdBuilder() {
  const { adSetId } = useParams<{ adSetId: string }>();
  const nav = useNavigate();
  const [account, setAccount] = useState<MetaAdAccount | null>(null);
  const [adSet, setAdSet] = useState<FbAdSet | null>(null);
  const [busy, setBusy] = useState(false);

  // Ad
  const [adName, setAdName] = useState('');
  const [adStatus, setAdStatus] = useState<'ACTIVE' | 'PAUSED'>('PAUSED');

  // Creative mode
  const [creativeMode, setCreativeMode] = useState<CreativeMode>('new');

  // Pick-existing
  const [existingCreatives, setExistingCreatives] = useState<FbCreative[] | null>(null);
  const [creativeSearch, setCreativeSearch] = useState('');
  const [pickedId, setPickedId] = useState<string>('');

  // New-inline creative
  const [creativeName, setCreativeName] = useState('');
  const [link, setLink] = useState('');
  const [headline, setHeadline] = useState('');
  const [description, setDescription] = useState('');
  const [message, setMessage] = useState('');
  const [mediaMode, setMediaMode] = useState<MediaMode>('image');
  const [imageUrl, setImageUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [cta, setCta] = useState<CreateAdCreativePayload['call_to_action_type']>('LEARN_MORE');
  const [aiModel, setAiModel] = useState<ModelPickerValue | undefined>(undefined);
  const [enhancing, setEnhancing] = useState(false);

  const enhance = async () => {
    setEnhancing(true);
    try {
      const r = await aiCampaignService.enhanceAdCopy({
        // adSet has campaign_id but not the objective directly; rely on backend
        // tone defaults if we don't have it locally.
        draftName: adName || undefined,
        draftHeadline: headline || undefined,
        draftDescription: description || undefined,
        draftMessage: message || undefined,
        landingPageUrl: link || undefined,
        ai_provider: aiModel?.provider,
        ai_model: aiModel?.model,
      });
      if (r.name)        setAdName(r.name);
      if (r.headline)    setHeadline(r.headline);
      if (r.description) setDescription(r.description);
      if (r.message)     setMessage(r.message);
      toast.success('Enhanced with AI');
    } catch {
      // Axios interceptor toasts the backend message; avoid duplicates.
    } finally { setEnhancing(false); }
  };

  useEffect(() => {
    facebookAdAccountService.get().then(setAccount).catch(() => undefined);
    if (adSetId) facebookAdSetsService.get(adSetId).then(setAdSet).catch(() => undefined);
  }, [adSetId]);

  // Load existing creatives when pick mode is selected
  useEffect(() => {
    if (creativeMode === 'pick' && existingCreatives === null) {
      facebookAdCreativesService.list().then((r) => setExistingCreatives(r.data)).catch(() => undefined);
    }
  }, [creativeMode, existingCreatives]);

  const filteredCreatives = (existingCreatives ?? []).filter((c) => {
    if (!creativeSearch) return true;
    const q = creativeSearch.toLowerCase();
    return c.name.toLowerCase().includes(q) || c.id.includes(q) || (c.headline ?? '').toLowerCase().includes(q);
  });

  const submit = async () => {
    setBusy(true);
    try {
      let creativeId: string;

      if (creativeMode === 'pick') {
        if (!pickedId) { toast.error('Pick a creative first'); setBusy(false); return; }
        creativeId = pickedId;
      } else {
        const payload: CreateAdCreativePayload = {
          name: creativeName || `${adName} creative`,
          link,
          headline: headline || undefined,
          description: description || undefined,
          message: message || undefined,
          call_to_action_type: cta,
        };
        if (mediaMode === 'image') {
          payload.image_url = imageUrl || undefined;
        } else {
          payload.video_url = videoUrl || undefined;
          payload.thumbnail_url = thumbnailUrl || undefined;
        }
        const c = await facebookAdCreativesService.create(payload);
        creativeId = c.id;
      }

      await facebookAdsService.create({
        name: adName,
        adset_id: adSetId!,
        creative_id: creativeId,
        status: adStatus,
      });
      toast.success(`Ad created · status ${adStatus}`);
      nav(`/facebook/ad-sets/${adSetId}`);
    } catch {
      /* error toast from interceptor */
    } finally {
      setBusy(false);
    }
  };

  const newCreativeValid =
    creativeMode === 'new' &&
    !!link &&
    (mediaMode === 'image' ? !!imageUrl : !!videoUrl);

  const ready = !!adName && (creativeMode === 'pick' ? !!pickedId : newCreativeValid);
  const pageMissing = !!account && !account.pageId;

  return (
    <>
      <Topbar
        title="New Ad"
        breadcrumb={`Facebook Ads / Ad Set / ${adSet?.name ?? '…'} / New Ad`}
        right={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => nav(`/facebook/ad-sets/${adSetId}`)}>
              Cancel
            </Button>
            <Button loading={busy} disabled={!ready || pageMissing} onClick={submit}>
              Create ad
            </Button>
          </div>
        }
      />

      <div
        className={clsx(
          'mx-auto w-full px-7 py-7',
          creativeMode === 'new' ? 'max-w-[1240px]' : 'max-w-[800px]',
        )}
      >
        <div
          className={clsx(
            'grid gap-6',
            creativeMode === 'new' ? 'lg:grid-cols-[1fr_360px]' : 'grid-cols-1',
          )}
        >
        <div className="space-y-4 min-w-0">
        {/* Page ID warning */}
        {pageMissing && (
          <div className="flex items-start gap-3 rounded-xl border border-warning/40 bg-warning-soft px-4 py-4">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-warning" />
            <div className="text-sm">
              <strong className="text-text-primary">Connect a Facebook Page first.</strong>{' '}
              <span className="text-text-secondary">
                Ads must come "from" a Page on Meta. Go to{' '}
                <Link to="/facebook/ad-account" className="text-primary underline">
                  Ad Account settings
                </Link>{' '}
                → click <em>Edit</em> on Connected Profiles → enter your Page ID. Then come back here.
              </span>
            </div>
          </div>
        )}

        {/* Ad details */}
        <Card>
          <SectionHeader title="Ad details" icon={<Megaphone size={16} />} />
          <FormField label="Ad name" required>
            <Input
              value={adName}
              onChange={(e) => setAdName(e.target.value)}
              placeholder="UAE Adults — Black Friday image"
            />
          </FormField>
          <FormField label="Initial status">
            <Select value={adStatus} onChange={(e) => setAdStatus(e.target.value as 'ACTIVE' | 'PAUSED')}>
              <option value="PAUSED">Paused — review before activating</option>
              <option value="ACTIVE">Active — go live immediately</option>
            </Select>
          </FormField>
        </Card>

        {/* Creative mode picker */}
        <Card>
          <SectionHeader
            title="Creative"
            subtitle="What people will see in their feed"
            icon={<ImageIcon size={16} />}
            action={
              <a
                href="/facebook/creatives"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1 text-xs text-text-muted hover:text-primary"
              >
                <ExternalLink size={12} />
                Manage creatives
              </a>
            }
          />

          {/* Tab switcher */}
          <div className="mb-5 flex gap-1 rounded-xl border border-border bg-surface-alt p-1">
            <CreativeModeTab
              active={creativeMode === 'new'}
              icon={<Plus size={13} />}
              label="Create new"
              onClick={() => setCreativeMode('new')}
            />
            <CreativeModeTab
              active={creativeMode === 'pick'}
              icon={<Search size={13} />}
              label="Use existing"
              onClick={() => setCreativeMode('pick')}
            />
          </div>

          {creativeMode === 'pick' ? (
            /* ---- Pick existing ---- */
            <div className="space-y-3">
              <Input
                value={creativeSearch}
                onChange={(e) => setCreativeSearch(e.target.value)}
                placeholder="Search creatives…"
                iconLeft={<Search size={14} />}
              />
              {existingCreatives === null ? (
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => <div key={i} className="skeleton h-16 rounded-xl" />)}
                </div>
              ) : filteredCreatives.length === 0 ? (
                <div className="rounded-xl border border-border bg-surface-alt py-8 text-center text-sm text-text-muted">
                  No creatives found.{' '}
                  <button
                    type="button"
                    className="text-primary underline"
                    onClick={() => setCreativeMode('new')}
                  >
                    Create one inline
                  </button>{' '}
                  or{' '}
                  <a
                    href="/facebook/creatives/new"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary underline"
                  >
                    open Creative Builder
                  </a>
                  .
                </div>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto rounded-xl border border-border bg-surface-alt p-2">
                  {filteredCreatives.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setPickedId(c.id)}
                      className={clsx(
                        'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                        pickedId === c.id
                          ? 'bg-primary-soft border border-primary'
                          : 'bg-surface hover:bg-surface-alt border border-transparent',
                      )}
                    >
                      {/* Thumbnail */}
                      <div className="flex h-12 w-20 shrink-0 overflow-hidden rounded-md bg-surface-alt">
                        {c.imagekit_url || c.thumbnail_url || c.image_url ? (
                          <img
                            src={c.imagekit_url || c.thumbnail_url || c.image_url}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-text-muted">
                            <ImageIcon size={16} />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-text-primary">{c.name}</div>
                        {c.headline && (
                          <div className="truncate text-xs text-text-secondary">{c.headline}</div>
                        )}
                        <div className="mt-0.5 font-mono text-2xs text-text-muted">{c.id}</div>
                      </div>
                      {pickedId === c.id && (
                        <Check size={16} className="shrink-0 text-primary" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* ---- New inline creative ---- */
            <div className="space-y-0">
              {/* AI enhance bar */}
              <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary-soft/40 p-2.5">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary text-white">
                  <Wand2 size={12} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-text-primary">Enhance with AI</div>
                  <div className="text-2xs text-text-muted">
                    Type rough drafts in the headline / description / message below, then click Enhance to polish them all at once.
                  </div>
                </div>
                <ModelPicker value={aiModel} onChange={setAiModel} size="sm" label="AI model" />
                <Button
                  variant="primary"
                  size="sm"
                  onClick={enhance}
                  loading={enhancing}
                  iconLeft={<Wand2 size={12} />}
                >
                  Enhance
                </Button>
              </div>

              <FormField label="Creative name (internal)" hint="Auto-generated from ad name if blank">
                <Input
                  value={creativeName}
                  onChange={(e) => setCreativeName(e.target.value)}
                  placeholder="Black Friday creative"
                />
              </FormField>
              <FormField label="Destination URL" required>
                <Input
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  placeholder="https://your-site.com/black-friday"
                  iconLeft={<Link2 size={14} />}
                />
              </FormField>

              {/* Media type toggle */}
              <FormField label="Media type">
                <div className="flex gap-1 rounded-xl border border-border bg-surface-alt p-1">
                  <CreativeModeTab
                    active={mediaMode === 'image'}
                    icon={<ImageIcon size={13} />}
                    label="Image"
                    onClick={() => setMediaMode('image')}
                  />
                  <CreativeModeTab
                    active={mediaMode === 'video'}
                    icon={<Film size={13} />}
                    label="Video"
                    onClick={() => setMediaMode('video')}
                  />
                </div>
              </FormField>

              {mediaMode === 'image' ? (
                <FormField label="Image">
                  <MediaUpload
                    mode="image"
                    value={imageUrl}
                    onChange={setImageUrl}
                    folder="fb-creatives"
                  />
                </FormField>
              ) : (
                <>
                  <FormField label="Video (MP4)">
                    <MediaUpload
                      mode="video"
                      value={videoUrl}
                      onChange={setVideoUrl}
                      folder="fb-creatives"
                    />
                  </FormField>
                  <FormField label="Thumbnail" hint="Optional">
                    <MediaUpload
                      mode="image"
                      value={thumbnailUrl}
                      onChange={setThumbnailUrl}
                      folder="fb-creatives"
                    />
                  </FormField>
                </>
              )}

              <FormField label="Headline" hint="≤255 chars">
                <Input
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  placeholder="25% OFF Black Friday"
                  maxLength={255}
                />
              </FormField>
              <FormField label="Description" hint="≤255 chars">
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={255}
                />
              </FormField>
              <FormField label={`Message / body text (${message.length}/2200)`}>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  maxLength={2200}
                  placeholder="VIP early access to our Black Friday sale — up to 25% off everything."
                  className="w-full resize-y rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-text-muted"
                />
              </FormField>
              <FormField label="Call-to-action button">
                <Select value={cta} onChange={(e) => setCta(e.target.value as any)}>
                  {CTAS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              </FormField>
            </div>
          )}
        </Card>
        </div>

        {/* ---- Right rail: live preview (only when building inline creative) ---- */}
        {creativeMode === 'new' && (
          <div className="lg:sticky lg:top-20 self-start space-y-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-text-muted">
              <Eye size={13} />
              Preview
            </div>
            <CreativePreview
              headline={headline}
              message={message}
              description={description}
              mediaUrl={mediaMode === 'image' ? imageUrl : thumbnailUrl}
              videoUrl={mediaMode === 'video' ? videoUrl : undefined}
              cta={cta ?? undefined}
              link={link}
            />
          </div>
        )}
        </div>
      </div>
    </>
  );
}

function CreativeModeTab({
  active, icon, label, onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold transition-all',
        active
          ? 'bg-surface text-text-primary shadow-soft'
          : 'text-text-muted hover:bg-surface hover:text-text-primary',
      )}
    >
      {icon}
      {label}
    </button>
  );
}
